import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import "./Home.css";

import heroVideo from "../assets/hero-video.mov";
import tatuandoSilueta from "../assets/tatuando-silueta.jpg";
import tattooProceso from "../assets/tattoo-proceso.jpg";
import tattooAguja from "../assets/tattoo-agujaprimerplano.jpg";

const containerVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: {
    x: "-100vw",
    transition: { ease: "easeInOut" },
  },
};

const NAV_LINKS = [
  { href: "#porque", label: "Sobre esto" },
  { href: "#metodo", label: "Método ALMA" },
  { href: "#cursos", label: "Productos" },
  { href: "#mentoria", label: "Mentoría 1:1" },
  { href: "#contacto", label: "Contacto" },
];

const DOLORES = [
  { num: "01", title: "No encontrás el estilo", text: "que realmente te representa." },
  { num: "02", title: "Cobrás menos", text: "de lo que querés." },
  { num: "03", title: "No sabés mostrar tu trabajo", text: "para que las personas entiendan lo que vale." },
];

const ALMA_ITEMS = [
  {
    index: "01",
    title: "Autenticidad",
    text: "Encontrar el estilo que realmente te representa, en vez de imitar lo que ya funciona para otros. (texto de ejemplo — a reemplazar por tu contenido real)",
  },
  {
    index: "02",
    title: "Lenguaje",
    text: "Aprender a comunicar ese estilo con claridad, para que las personas entiendan lo que hacés y por qué vale lo que cobrás. (texto de ejemplo)",
  },
  {
    index: "03",
    title: "Marca",
    text: "Construir una presencia coherente, dentro y fuera de tu trabajo, que sostenga esa identidad en el tiempo. (texto de ejemplo)",
  },
  {
    index: "04",
    title: "Autoridad",
    text: "Posicionarte como referente, para que las oportunidades y los clientes correctos te encuentren a vos. (texto de ejemplo)",
  },
];

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "curso", label: "Cursos" },
  { key: "guia", label: "Guías PDF" },
  { key: "producto", label: "Productos" },
];

const CURSOS = [
  {
    cat: "curso",
    tag: "Curso grabado · Nivel inicial",
    title: "Curso Clínico de Tatuaje",
    meta: ["Técnica", "Para arrancar"],
    text: "El curso técnico de base: bioseguridad, máquina, aguja, primeras líneas y primeras sesiones reales. El punto de partida antes de todo lo demás.",
    href: "#",
    cta: "Comprar con Mercado Pago",
  },
  {
    cat: "curso",
    tag: "Curso grabado · Método ALMA",
    title: "Desarrollo Artístico",
    meta: ["Estilo", "Marca", "Autoridad"],
    text: "Todo lo que un curso técnico no enseña: encontrar tu estilo, comunicarlo, construir marca y vivir de tu arte.",
    href: "#",
    cta: "Comprar con Mercado Pago",
  },
  {
    cat: "guia",
    tag: "Guía PDF · Disponible ahora",
    title: "La Mirada",
    meta: ["Acceso inmediato"],
    text: "(Introducción pendiente — a completar con tu contenido real.) Pago online, acceso inmediato al PDF.",
    href: "https://mpago.la/2wgsnjU",
    cta: "Comprar con Mercado Pago",
  },
  {
    cat: "producto",
    tag: "Producto digital · Próximamente",
    title: "Presets de edición",
    meta: ["Lightroom"],
    text: "El set que uso para mostrar mis piezas y mi trabajo con una estética consistente y reconocible.",
    href: "#contacto",
    cta: "Avisarme",
  },
];

const Home = () => {
  const rootRef = useRef(null);
  const cursorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [openAlma, setOpenAlma] = useState(null);
  const [formSent, setFormSent] = useState(false);

  // custom cursor — solo en dispositivos con mouse
  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return;
    const cursor = cursorRef.current;
    const move = (e) => {
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
    };
    const grow = () => cursor.classList.add("grow");
    const shrink = () => cursor.classList.remove("grow");

    window.addEventListener("mousemove", move);
    const targets = rootRef.current.querySelectorAll("a, button, .card, .trio-item");
    targets.forEach((el) => {
      el.addEventListener("mouseenter", grow);
      el.addEventListener("mouseleave", shrink);
    });

    return () => {
      window.removeEventListener("mousemove", move);
      targets.forEach((el) => {
        el.removeEventListener("mouseenter", grow);
        el.removeEventListener("mouseleave", shrink);
      });
    };
  }, []);

  // scroll reveal — reemplaza al ScrollTrigger de gsap del index.html original
  useEffect(() => {
    const els = rootRef.current.querySelectorAll(".reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeFilter]);

  const closeMobileMenu = () => setMenuOpen(false);
  const toggleAlma = (i) => setOpenAlma((prev) => (prev === i ? null : i));

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSent(true);
    e.target.reset();
  };

  return (
    <motion.main
      ref={rootRef}
      className="aldana-landing"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div ref={cursorRef} className="landing-cursor" />

      <header className="landing-header">
        <div className="landing-logo">Aldana Selena</div>
        <nav className="desktop-nav">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
        </nav>
        <button className="menu-btn" onClick={() => setMenuOpen((v) => !v)}>
          {menuOpen ? "Cerrar" : "Menú"}
        </button>
      </header>

      <div className={`mobile-menu${menuOpen ? " open" : ""}`}>
        {NAV_LINKS.map((link) => (
          <a key={link.href} href={link.href} onClick={closeMobileMenu}>
            {link.label}
          </a>
        ))}
      </div>

      {/* HERO */}
      <section className="landing-hero">
        <div className="hero-media reveal">
          <video autoPlay muted loop playsInline poster={tatuandoSilueta}>
            <source src={heroVideo} type="video/mp4" />
          </video>
        </div>
        <div className="hero-copy">
          <span className="eyebrow reveal">Productos digitales · Cursos · Mentoría 1:1</span>
          <h1 className="reveal">Aldana Selena</h1>
          <p className="reveal">
            Durante años pensé que crecer como artista dependía únicamente de mejorar la técnica. Hoy comparto el
            Método ALMA: cómo encontrar tu estilo, comunicarlo y vivir de tu arte.
          </p>
          <p className="landing-quote reveal">"Arte creado para sentirse. Identidad construida para trascender."</p>
          <div className="btn-row reveal">
            <a href="#cursos" className="btn-pill">
              Ver productos
            </a>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <div className="cta-band reveal">
        <span>¿Querés encontrar tu estilo y vivir de tu arte?</span>
      </div>

      {/* TRIO GRID */}
      <div className="trio-grid">
        <a
          href="#metodo"
          className="trio-item reveal"
          style={{ backgroundImage: `url(${tatuandoSilueta})`, backgroundPosition: "15% 15%" }}
        >
          <span>Método ALMA</span>
        </a>
        <a href="#cursos" className="trio-item reveal" style={{ backgroundImage: `url(${tattooProceso})` }}>
          <span>Productos digitales</span>
        </a>
        <a href="#mentoria" className="trio-item reveal" style={{ backgroundImage: `url(${tattooAguja})` }}>
          <span>Mentoría 1:1</span>
        </a>
      </div>

      {/* POR QUE */}
      <section id="porque" className="landing-porque">
        <div className="section-head">
          <div className="eyebrow reveal">Por qué enseño esto</div>
          <h2 className="reveal">
            No alcanza con
            <br />
            mejorar la técnica.
          </h2>
        </div>
        <div className="wrap reveal">
          <p>
            Durante años pensé que crecer como artista dependía únicamente de mejorar <strong>la técnica</strong>.
          </p>
          <p>
            Pero mi carrera también se construyó explorando imagen, fotografía, comunicación, posicionamiento y
            formas de transmitir una visión.
          </p>
          <p>
            Entender eso cambió <strong>cómo me mostraba, cuánto cobraba</strong> y las oportunidades que aparecieron.
          </p>
          <p>Hoy gran parte de lo que comparto nace de ese recorrido.</p>
        </div>
      </section>

      {/* DOLORES */}
      <section id="dolores" className="landing-dolores">
        <div className="section-head" style={{ paddingTop: 0 }}>
          <div className="eyebrow reveal">¿Te está frenando alguna de estas cosas?</div>
          <h2 className="reveal">
            Quizás ya sos buena
            <br />
            técnicamente.
          </h2>
        </div>
        <div className="dolor-list">
          {DOLORES.map((d) => (
            <div className="dolor-item reveal" key={d.num}>
              <div className="dolor-num">{d.num}</div>
              <div>
                <h4>{d.title}</h4>
                <span>{d.text}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* METODO ALMA */}
      <section id="metodo" className="landing-metodo">
        <div className="section-head">
          <div className="eyebrow reveal">El proceso</div>
          <h2 className="reveal">Método ALMA</h2>
          <p className="alma-tagline reveal">
            Encontrá tu estilo · Aprendé a comunicarlo · Posicionate · Viví de tu arte
          </p>
        </div>
        <div className="alma-list reveal">
          {ALMA_ITEMS.map((item, i) => (
            <div className={`alma-item${openAlma === i ? " open" : ""}`} key={item.index}>
              <button className="alma-q" onClick={() => toggleAlma(i)}>
                <span className="alma-index">{item.index}</span>
                <span className="alma-title">{item.title}</span>
                <span className="plus">+</span>
              </button>
              <div className="alma-a">
                <div className="alma-a-inner">
                  <p>{item.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTOS DIGITALES */}
      <section id="cursos" className="landing-cursos">
        <div className="section-head">
          <div className="eyebrow reveal">Productos digitales</div>
          <h2 className="reveal">
            Aprendé a tu ritmo,
            <br />
            con el Método ALMA
          </h2>
        </div>
        <div className="filters reveal">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={activeFilter === f.key ? "active" : ""}
              onClick={() => setActiveFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="cards-grid wrap">
          {CURSOS.map((c) => (
            <div
              className={`card reveal${activeFilter === "all" || activeFilter === c.cat ? "" : " is-hidden"}`}
              key={c.title}
            >
              <span className="tag">{c.tag}</span>
              <h3>{c.title}</h3>
              <div className="meta">
                {c.meta.map((m) => (
                  <span key={m}>{m}</span>
                ))}
              </div>
              <p>{c.text}</p>
              <a
                className="card-btn"
                href={c.href}
                target={c.href.startsWith("#") ? undefined : "_blank"}
                rel="noopener noreferrer"
              >
                {c.cta}
              </a>
            </div>
          ))}
        </div>
        <p className="cursos-note">
          Pago online con Mercado Pago · Guía PDF disponible ahora en Hotmart. Links de pago pendientes de conectar.
        </p>
      </section>

      {/* MENTORIA */}
      <section id="mentoria" className="landing-mentoria">
        <div className="section-head">
          <div className="eyebrow reveal">Mentoría individual</div>
          <h2 className="reveal">
            Un acompañamiento
            <br />
            1:1, cercano.
          </h2>
        </div>
        <div className="mentoria-panel reveal" style={{ backgroundImage: `url(${tattooAguja})` }}>
          <div className="mentoria-panel-inner wrap">
            <div className="mentoria-tags">
              <span>Acceso limitado</span>
              <span>·</span>
              <span>Aplicación previa requerida</span>
            </div>
            <h3>Mentorías 1:1 · Online / Presencial</h3>
            <p className="quote">"No esperes tener todo listo. El momento es ahora."</p>
            <p className="note">
              Un espacio 1:1 basado en el Método ALMA: autenticidad, lenguaje, marca y autoridad, aplicado a tu
              propio camino como artista.
            </p>
            <p className="note">
              Cupos limitados · lista de espera activa. Quienes estén en lista tendrán acceso prioritario cuando se
              abran los próximos cupos.
            </p>
            <a href="https://mpago.la/2Mdc2Li" target="_blank" rel="noopener noreferrer" className="btn-pill">
              Reservar mi lugar →
            </a>
          </div>
        </div>
      </section>

      {/* CONTACTO */}
      <section id="contacto" className="landing-contacto">
        <div className="eyebrow reveal">Contacto</div>
        <h2 className="landing-contacto-title reveal">
          Sumate a la lista
          <br />
          de espera.
        </h2>
        <form onSubmit={handleSubmit}>
          <input type="text" placeholder="Nombre" required />
          <input type="email" placeholder="Email" required />
          <textarea rows={4} placeholder="Contame en qué estás trabajando ahora" />
          <button type="submit" className="btn-pill">
            Enviar
          </button>
          {formSent && <div className="form-note">Gracias — te voy a responder pronto.</div>}
        </form>
      </section>

    </motion.main>
  );
};

export default Home;
